/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import axios from "axios";

import getOpco from "utils/getOpco";

import { API_URL, PPE_V1_PATH, PPE_V2_PATH } from "config";

import { isFeatureActive } from "utils/getFeature";
import CreditCardSlice, { CreditCardsSliceFromRoot } from "types/CreditCardSlice";
import { setIframeLayer, setLoader, setModal } from "state/config-slice";
import { setDebugTransactions } from "utils/debugHelper";
import CreditCardsSlice from "types/CreditCardSlice";

const PATH = isFeatureActive("PPE 1.0 Backend") ? JSON.parse(PPE_V1_PATH)["v2"] : PPE_V2_PATH;

const initialState: CreditCardSlice = {
  loaded: false,
  list: [],
};

export const fetchCreditCards = createAsyncThunk("cart/fetchCreditCards", async (_arg, { dispatch }: any) => {
  try {
    const result = await axios.get(`${API_URL}${PATH}/payments/methods?opco=${getOpco()}&marketplace=true`, {
      withCredentials: true,
      headers: {
        Accept: "application/json; charset=utf-8",
        "Content-Type": "application/json; charset=utf-8",
      },
    });

    if (result.status === 200) {
      dispatch(setCreditCards(result.data));
      return result.data;
    }

    setDebugTransactions(result);
    return result.data;
  } catch (error) {
    dispatch(setLoader({ showLoader: false, messageHeader: "", messageText: "" }));

    dispatch(
      setModal({
        showModal: true,
        errorModal: true,
      }),
    );

    return Promise.reject(error);
  }
});

export const addCreditCard = createAsyncThunk("cart/addCreditCard", async (_arg, { dispatch }) => {
  try {
    const result = await axios.get(`${API_URL}${PATH}/payments/addMethod?opco=${getOpco()}&marketplace=true`, {
      withCredentials: true,
      headers: {
        Accept: "application/json; charset=utf-8",
        "Content-Type": "application/json; charset=utf-8",
      },
    });

    if (result.status === 200) {
      const { managePaymentMethodsUrl } = result.data;
      if (managePaymentMethodsUrl) {
        dispatch(setIframeLayer({ showIframeLayer: true, url: managePaymentMethodsUrl }));
      }

      return result.data;
    }

    setDebugTransactions(result);
    return result.data;
  } catch (error) {
    dispatch(setLoader({ showLoader: false, messageHeader: "", messageText: "" }));

    dispatch(
      setModal({
        showModal: true,
        errorModal: true,
      }),
    );

    return Promise.reject(error);
  }
});

export const editCreditCard = createAsyncThunk(
  "cart/editCreditCard",
  async (cardPaymentId: string | undefined, { dispatch }) => {
    try {
      if (!cardPaymentId) throw new Error("Missing Payment Id!");

      const result = await axios.get(
        `${API_URL}${PATH}/payments/editMethod?cardPaymentId=${cardPaymentId}&opco=${getOpco()}&marketplace=true`,
        {
          withCredentials: true,
          headers: {
            Accept: "application/json; charset=utf-8",
            "Content-Type": "application/json; charset=utf-8",
          },
        },
      );

      if (result.status === 200) {
        const { managePaymentMethodsUrl } = result.data;
        if (managePaymentMethodsUrl) {
          dispatch(setIframeLayer({ showIframeLayer: true, url: managePaymentMethodsUrl }));
        }

        return result.data;
      }

      setDebugTransactions(result);
      return result.data;
    } catch (error) {
      dispatch(setLoader({ showLoader: false, messageHeader: "", messageText: "" }));

      dispatch(
        setModal({
          showModal: true,
          errorModal: true,
        }),
      );

      return Promise.reject(error);
    }
  },
);

export const creditCardsSlice = createSlice({
  name: "CreditCards",
  initialState,
  reducers: {
    setCreditCards: (state, action) => {
      const retrievedCreditCards = [...action.payload];

      return {
        ...state,
        loaded: true,
        list: retrievedCreditCards,
      };
    },
  },
});

export const { setCreditCards } = creditCardsSlice.actions;

export const creditCardsState = (state: CreditCardsSliceFromRoot): CreditCardsSlice => state.creditCards;

export default creditCardsSlice.reducer;
